import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  MessageSquare,
  MessageCircle,
  Facebook,
  Twitter,
  Instagram,
  Smartphone,
  Phone,
  Settings,
  TestTube2,
  Loader2,
  Check,
  X,
  AlertCircle,
  Eye,
  EyeOff,
  Trash2,
  ChevronDown,
  ChevronRight,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { Button, Card, Spinner } from '@centraoneplus/ui';
import { useAutoDismiss } from '@/hooks/useAutoDismiss';
import { sanitizePhoneInput } from '@/lib/phone';
import { LIVE_SOCIAL_CHANNELS_KEY } from '@components/layouts/useLiveSocialChannels';
import {
  validateWhatsApp,
  validateFacebook,
  validateTwitter,
  validateInstagram,
  validateSms,
  hasErrors,
  type FieldErrors,
} from './socialChannelValidation';
import {
  whatsappService,
  facebookService,
  twitterService,
  instagramService,
  smsService,
  type WhatsAppConfig,
  type WhatsAppCannedReply,
  type WhatsAppTemplate,
  type FacebookConfig,
  type TwitterConfig,
  type InstagramConfig,
  type SmsConfig,
} from '@/services/social-channels.service';

const ChannelIcons = {
  whatsapp: MessageCircle,
  facebook: Facebook,
  twitter: Twitter,
  instagram: Instagram,
  sms: Smartphone,
};

type ChannelType = 'whatsapp' | 'facebook' | 'twitter' | 'instagram' | 'sms';

interface ChannelStatus {
  whatsapp: WhatsAppConfig | null;
  facebook: FacebookConfig | null;
  twitter: TwitterConfig | null;
  instagram: InstagramConfig | null;
  sms: SmsConfig | null;
}

export default function SocialChannelSettings() {
  const [isLoading, setIsLoading] = useState(true);
  const [configs, setConfigs] = useState<ChannelStatus>({
    whatsapp: null,
    facebook: null,
    twitter: null,
    instagram: null,
    sms: null,
  });
  const [expandedChannel, setExpandedChannel] = useState<ChannelType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const loadConfigs = useCallback(async () => {
    try {
      setIsLoading(true);
      const [whatsapp, facebook, twitter, instagram, sms] = await Promise.all([
        whatsappService.getConfig(),
        facebookService.getConfig(),
        twitterService.getConfig(),
        instagramService.getConfig(),
        smsService.getConfig(),
      ]);
      setConfigs({ whatsapp, facebook, twitter, instagram, sms });
      // The nav rail lists WhatsApp / Facebook only while they are live.
      void queryClient.invalidateQueries({ queryKey: LIVE_SOCIAL_CHANNELS_KEY });
    } catch (err) {
      setError('Failed to load channel configurations');
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  // Auto-dismiss the load error after 5s (also dismissible via its X button).
  useAutoDismiss(error, () => setError(null));

  const toggleChannel = (channel: ChannelType) => {
    setExpandedChannel(expandedChannel === channel ? null : channel);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Social & Messaging Channels
        </h2>
        <p className="text-sm text-gray-500 mt-1 dark:text-gray-400">
          Configure your social media and messaging integrations to receive and respond to customer messages.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-300">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            aria-label="Dismiss"
            className="flex-shrink-0 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="space-y-4">
        {/* WhatsApp */}
        <ChannelCard
          channel="whatsapp"
          label="WhatsApp Business"
          description="Connect your WhatsApp Business account to receive customer messages."
          icon={ChannelIcons.whatsapp}
          config={configs.whatsapp}
          expanded={expandedChannel === 'whatsapp'}
          onToggle={() => toggleChannel('whatsapp')}
          onRefresh={loadConfigs}
        >
          <WhatsAppForm config={configs.whatsapp} onSave={loadConfigs} />
          {configs.whatsapp && <WhatsAppAutoReplyForm />}
          {configs.whatsapp && <WhatsAppTemplatesPanel />}
        </ChannelCard>

        {/* Facebook Messenger */}
        <ChannelCard
          channel="facebook"
          label="Facebook Messenger"
          description="Connect your Facebook Page to receive Messenger conversations."
          icon={ChannelIcons.facebook}
          config={configs.facebook}
          expanded={expandedChannel === 'facebook'}
          onToggle={() => toggleChannel('facebook')}
          onRefresh={loadConfigs}
        >
          <FacebookForm config={configs.facebook} onSave={loadConfigs} />
        </ChannelCard>

        {/* Twitter */}
        <ChannelCard
          channel="twitter"
          label="Twitter / X"
          description="Connect your Twitter account to receive Direct Messages."
          icon={ChannelIcons.twitter}
          config={configs.twitter}
          expanded={expandedChannel === 'twitter'}
          onToggle={() => toggleChannel('twitter')}
          onRefresh={loadConfigs}
        >
          <TwitterForm config={configs.twitter} onSave={loadConfigs} />
        </ChannelCard>

        {/* Instagram */}
        <ChannelCard
          channel="instagram"
          label="Instagram Direct"
          description="Connect your Instagram Business account to receive Direct Messages."
          icon={ChannelIcons.instagram}
          config={configs.instagram}
          expanded={expandedChannel === 'instagram'}
          onToggle={() => toggleChannel('instagram')}
          onRefresh={loadConfigs}
        >
          <InstagramForm config={configs.instagram} onSave={loadConfigs} />
        </ChannelCard>

        {/* SMS */}
        <ChannelCard
          channel="sms"
          label="SMS (Twilio)"
          description="Configure SMS messaging via Twilio for customer communications."
          icon={ChannelIcons.sms}
          config={configs.sms}
          expanded={expandedChannel === 'sms'}
          onToggle={() => toggleChannel('sms')}
          onRefresh={loadConfigs}
        >
          <SmsForm config={configs.sms} onSave={loadConfigs} />
        </ChannelCard>
      </div>
    </div>
  );
}

// ==================== Channel Card Component ====================

interface ChannelCardProps {
  channel: ChannelType;
  label: string;
  description: string;
  icon: React.ElementType;
  config: any | null;
  expanded: boolean;
  onToggle: () => void;
  onRefresh: () => void;
  children: React.ReactNode;
}

function ChannelCard({ label, description, icon: Icon, config, expanded, onToggle, children }: ChannelCardProps) {
  return (
    <Card className="overflow-hidden">
      <div
        className="p-4 flex items-center justify-between gap-3 cursor-pointer rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
        onClick={onToggle}
      >
        <div className="flex min-w-0 items-center gap-3">
          <Icon className="h-6 w-6 flex-shrink-0 text-[var(--co-ink-3)]" aria-hidden="true" />
          <div className="min-w-0">
            <h3 className="font-medium">{label}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          <span className={`whitespace-nowrap px-2 py-1 text-xs rounded-full ${(config?.enabled ?? config?.isActive) ? 'bg-green-100 text-green-700' : config ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'} dark:bg-gray-800 dark:text-gray-400`}>
            {(config?.enabled ?? config?.isActive) ? 'Active' : config ? 'Configured' : 'Not configured'}
          </span>
          {expanded ? <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" /> : <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t p-6 bg-gray-50 dark:bg-gray-900">
          {children}
        </div>
      )}
    </Card>
  );
}

// ==================== WhatsApp Form ====================

function WhatsAppForm({ config, onSave }: { config: WhatsAppConfig | null; onSave: () => void }) {
  // Only what the Meta Cloud API send call actually needs. The access token is
  // write-only: the backend returns it masked, and an empty field on update
  // means "keep the stored token".
  const [formData, setFormData] = useState({
    apiVersion: config?.apiVersion || 'v25.0',
    phoneNumberId: config?.phoneNumberId || '',
    accessToken: '',
    webhookVerifyToken: config?.webhookVerifyToken || '',
    businessAccountId: config?.businessAccountId || '',
    // Blank on purpose when unset: this organization then runs on ours.
    apiEndpoint: config?.apiEndpoint || '',
    isActive: config?.isActive ?? true,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  const errors = useMemo(() => validateWhatsApp(formData, !!config?.hasAccessToken), [formData, config?.hasAccessToken]);
  const field = useFieldErrors(errors);

  const handleSave = async () => {
    if (hasErrors(errors)) return;
    try {
      setTestResult(null);
      setIsSaving(true);
      const payload = {
        phoneNumberId: formData.phoneNumberId.trim(),
        webhookVerifyToken: formData.webhookVerifyToken.trim(),
        businessAccountId: formData.businessAccountId.trim(),
        isActive: formData.isActive,
        // Both omitted entirely when blank: a blank token means "keep the
        // stored one", and a blank version would fail the vNN.N format check.
        ...(formData.accessToken.trim() ? { accessToken: formData.accessToken.trim() } : {}),
        ...(formData.apiVersion.trim() ? { apiVersion: formData.apiVersion.trim() } : {}),
        // Always sent, unlike the token: an emptied box means "stop using our
        // own endpoint and fall back to the system one".
        apiEndpoint: formData.apiEndpoint.trim(),
      };
      if (config) {
        await whatsappService.updateConfig(payload);
      } else {
        await whatsappService.createConfig(payload);
      }
      setFormData((prev) => ({ ...prev, accessToken: '' }));
      field.reset();
      onSave();
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data?.message || 'Failed to save' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setIsTesting(true);
      const result = await whatsappService.testConnection();
      setTestResult(result);
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.response?.data?.message || err.message || 'Connection test failed',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete WhatsApp configuration?')) return;
    await whatsappService.deleteConfig();
    onSave();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          label="WhatsApp API Version *"
          value={formData.apiVersion}
          onChange={(v) => setFormData({ ...formData, apiVersion: v })}
          placeholder="v25.0"
          {...field.props('apiVersion')}
        />
        <FormField
          label="WhatsApp Phone Number ID *"
          value={formData.phoneNumberId}
          // Meta's Phone Number ID is purely numeric.
          onChange={(v) => setFormData({ ...formData, phoneNumberId: v.replace(/\D/g, '') })}
          placeholder="1318611074664832"
          {...field.props('phoneNumberId')}
        />
        <PasswordField
          label={config?.hasAccessToken ? 'WhatsApp Access Token (stored)' : 'WhatsApp Access Token *'}
          value={formData.accessToken}
          onChange={(v) => setFormData({ ...formData, accessToken: v })}
          show={showPasswords.accessToken}
          onToggle={() => setShowPasswords({ ...showPasswords, accessToken: !showPasswords.accessToken })}
          {...field.props('accessToken')}
        />
        <FormField
          label="WhatsApp Webhook Verify Token"
          value={formData.webhookVerifyToken}
          onChange={(v) => setFormData({ ...formData, webhookVerifyToken: v })}
          placeholder="Token you enter in Meta's webhook setup"
          {...field.props('webhookVerifyToken')}
        />
        <FormField
          label="WhatsApp Business Account ID (optional)"
          value={formData.businessAccountId}
          onChange={(v) => setFormData({ ...formData, businessAccountId: v })}
          placeholder="Only needed to sync templates"
          {...field.props('businessAccountId')}
        />
      </div>
      {config?.hasAccessToken && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Current token {config.accessTokenMasked}. Leave the field blank to keep it.
        </p>
      )}

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            API Endpoint
          </h4>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Your organization&apos;s own Graph endpoint. When set it takes priority
            for every WhatsApp call this organization makes. Leave it empty to use
            the system endpoint.
          </p>
        </div>
        <FormField
          label="Endpoint URL (optional)"
          value={formData.apiEndpoint}
          onChange={(v) => setFormData({ ...formData, apiEndpoint: v })}
          placeholder="https://graph.facebook.com/v25.0/1372295532632089/messages"
          {...field.props('apiEndpoint')}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Paste the host on its own or a full messages URL - either works. The
          phone number id above is always used, and a <code>v25.0</code> segment
          sets the API version.
        </p>
        {config?.effectiveApiEndpoint && (
          <p className="text-xs text-gray-600 dark:text-gray-300">
            Currently sending to{' '}
            <code className="break-all">{config.effectiveApiEndpoint}</code>{' '}
            <span className="text-gray-500 dark:text-gray-400">
              ({config.apiEndpoint ? 'your endpoint' : 'system endpoint'})
            </span>
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.isActive}
          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
          className="h-4 w-4 text-blue-600"
        />
        <span className="text-sm">Enable WhatsApp channel</span>
      </div>
      {testResult && <TestResultBanner result={testResult} onDismiss={() => setTestResult(null)} />}
      <FormActions config={config} isSaving={isSaving} isTesting={isTesting} onTest={handleTest} onSave={handleSave} onDelete={handleDelete} errors={errors} />
    </div>
  );
}

// ==================== Facebook Form ====================

function FacebookForm({ config, onSave }: { config: FacebookConfig | null; onSave: () => void }) {
  // Token and secret are write-only: the backend returns them masked, and a
  // blank field on update means "keep the stored one".
  const [formData, setFormData] = useState({
    pageId: config?.pageId || '',
    pageName: config?.pageName || '',
    pageAccessToken: '',
    appId: config?.appId || '',
    appSecret: '',
    webhookVerifyToken: config?.webhookVerifyToken || '',
    isActive: config?.isActive ?? true,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  const errors = useMemo(() => validateFacebook(formData, !!config?.hasPageAccessToken), [formData, config?.hasPageAccessToken]);
  const field = useFieldErrors(errors);

  const handleSave = async () => {
    if (hasErrors(errors)) return;
    try {
      setIsSaving(true);
      setTestResult(null);
      const payload = {
        pageId: formData.pageId.trim(),
        pageName: formData.pageName.trim(),
        appId: formData.appId.trim(),
        webhookVerifyToken: formData.webhookVerifyToken.trim(),
        isActive: formData.isActive,
        // Omitted entirely when blank, so an update keeps what is stored.
        ...(formData.pageAccessToken.trim()
          ? { pageAccessToken: formData.pageAccessToken.trim() }
          : {}),
        ...(formData.appSecret.trim() ? { appSecret: formData.appSecret.trim() } : {}),
      };
      if (config) {
        await facebookService.updateConfig(payload);
      } else {
        await facebookService.createConfig(payload);
      }
      setFormData((prev) => ({ ...prev, pageAccessToken: '', appSecret: '' }));
      field.reset();
      onSave();
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data?.message || 'Failed to save' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setIsTesting(true);
      const result = await facebookService.testConnection();
      setTestResult(result);
    } catch (err: any) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setIsTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete Facebook configuration?')) return;
    await facebookService.deleteConfig();
    onSave();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Page ID *" value={formData.pageId} onChange={(v) => setFormData({ ...formData, pageId: v.replace(/\D/g, '') })} placeholder="1234567890" {...field.props('pageId')} />
        <FormField label="Page Name *" value={formData.pageName} onChange={(v) => setFormData({ ...formData, pageName: v })} placeholder="My Page" {...field.props('pageName')} />
        <FormField label="App ID *" value={formData.appId} onChange={(v) => setFormData({ ...formData, appId: v })} placeholder="1234567890" {...field.props('appId')} />
        <PasswordField label={config?.hasPageAccessToken ? 'Page Access Token (stored)' : 'Page Access Token *'} value={formData.pageAccessToken} onChange={(v) => setFormData({ ...formData, pageAccessToken: v })} show={showPasswords.pageAccessToken} onToggle={() => setShowPasswords({ ...showPasswords, pageAccessToken: !showPasswords.pageAccessToken })} {...field.props('pageAccessToken')} />
        <PasswordField label={config?.hasAppSecret ? 'App Secret (stored)' : 'App Secret'} value={formData.appSecret} onChange={(v) => setFormData({ ...formData, appSecret: v })} show={showPasswords.appSecret} onToggle={() => setShowPasswords({ ...showPasswords, appSecret: !showPasswords.appSecret })} {...field.props('appSecret')} />
        <FormField label="Webhook Verify Token" value={formData.webhookVerifyToken} onChange={(v) => setFormData({ ...formData, webhookVerifyToken: v })} placeholder="Token you enter in Meta's webhook setup" {...field.props('webhookVerifyToken')} />
      </div>
      {config?.hasPageAccessToken && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Current token {config.pageAccessTokenMasked}. Leave the field blank to keep it.
        </p>
      )}
      {!config?.hasAppSecret && (
        <p className="text-xs text-amber-600 dark:text-amber-500">
          Without an App Secret the webhook cannot verify Meta&apos;s signature, so a forged
          payload cannot be told apart from a real one. Add it before going live.
        </p>
      )}
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} className="h-4 w-4 text-blue-600" />
        <span className="text-sm">Enable Facebook Messenger channel</span>
      </div>
      {testResult && <TestResultBanner result={testResult} onDismiss={() => setTestResult(null)} />}
      <FormActions config={config} isSaving={isSaving} isTesting={isTesting} onTest={handleTest} onSave={handleSave} onDelete={handleDelete} errors={errors} />
    </div>
  );
}

// ==================== Twitter Form ====================

function TwitterForm({ config, onSave }: { config: TwitterConfig | null; onSave: () => void }) {
  const [formData, setFormData] = useState({
    accountId: config?.accountId || '',
    accountHandle: config?.accountHandle || '',
    accountName: config?.accountName || '',
    bearerToken: '',
    apiKey: '',
    apiSecret: '',
    accessToken: '',
    accessSecret: '',
    enabled: config?.enabled || false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  const errors = useMemo(() => validateTwitter(formData, !config), [formData, config]);
  const field = useFieldErrors(errors);

  const handleSave = async () => {
    if (hasErrors(errors)) return;
    try {
      setIsSaving(true);
      if (config) {
        await twitterService.updateConfig(formData);
      } else {
        await twitterService.createConfig(formData);
      }
      onSave();
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data?.message || 'Failed to save' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setIsTesting(true);
      const result = await twitterService.testConnection();
      setTestResult(result);
    } catch (err: any) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setIsTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete Twitter configuration?')) return;
    await twitterService.deleteConfig();
    onSave();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Account ID *" value={formData.accountId} onChange={(v) => setFormData({ ...formData, accountId: v })} placeholder="1234567890" {...field.props('accountId')} />
        <FormField label="Account Handle *" value={formData.accountHandle} onChange={(v) => setFormData({ ...formData, accountHandle: v })} placeholder="@myaccount" {...field.props('accountHandle')} />
        <FormField label="Account Name" value={formData.accountName} onChange={(v) => setFormData({ ...formData, accountName: v })} placeholder="My Account" />
        <PasswordField label="Bearer Token *" value={formData.bearerToken} onChange={(v) => setFormData({ ...formData, bearerToken: v })} show={showPasswords.bearerToken} onToggle={() => setShowPasswords({ ...showPasswords, bearerToken: !showPasswords.bearerToken })} {...field.props('bearerToken')} />
        <PasswordField label="API Key *" value={formData.apiKey} onChange={(v) => setFormData({ ...formData, apiKey: v })} show={showPasswords.apiKey} onToggle={() => setShowPasswords({ ...showPasswords, apiKey: !showPasswords.apiKey })} {...field.props('apiKey')} />
        <PasswordField label="API Secret *" value={formData.apiSecret} onChange={(v) => setFormData({ ...formData, apiSecret: v })} show={showPasswords.apiSecret} onToggle={() => setShowPasswords({ ...showPasswords, apiSecret: !showPasswords.apiSecret })} {...field.props('apiSecret')} />
        <PasswordField label="Access Token *" value={formData.accessToken} onChange={(v) => setFormData({ ...formData, accessToken: v })} show={showPasswords.accessToken} onToggle={() => setShowPasswords({ ...showPasswords, accessToken: !showPasswords.accessToken })} {...field.props('accessToken')} />
        <PasswordField label="Access Secret *" value={formData.accessSecret} onChange={(v) => setFormData({ ...formData, accessSecret: v })} show={showPasswords.accessSecret} onToggle={() => setShowPasswords({ ...showPasswords, accessSecret: !showPasswords.accessSecret })} {...field.props('accessSecret')} />
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={formData.enabled} onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })} className="h-4 w-4 text-blue-600" />
        <span className="text-sm">Enable Twitter/X channel</span>
      </div>
      {testResult && <TestResultBanner result={testResult} onDismiss={() => setTestResult(null)} />}
      <FormActions config={config} isSaving={isSaving} isTesting={isTesting} onTest={handleTest} onSave={handleSave} onDelete={handleDelete} errors={errors} />
    </div>
  );
}

// ==================== Instagram Form ====================

function InstagramForm({ config, onSave }: { config: InstagramConfig | null; onSave: () => void }) {
  const [formData, setFormData] = useState({
    instagramAccountId: config?.instagramAccountId || '',
    pageId: config?.pageId || '',
    username: config?.username || '',
    accessToken: '',
    verifyToken: '',
    appSecret: '',
    enabled: config?.enabled || false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  const errors = useMemo(() => validateInstagram(formData, !config), [formData, config]);
  const field = useFieldErrors(errors);

  const handleSave = async () => {
    if (hasErrors(errors)) return;
    try {
      setIsSaving(true);
      if (config) {
        await instagramService.updateConfig(formData);
      } else {
        await instagramService.createConfig(formData);
      }
      onSave();
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data?.message || 'Failed to save' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setIsTesting(true);
      const result = await instagramService.testConnection();
      setTestResult(result);
    } catch (err: any) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setIsTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete Instagram configuration?')) return;
    await instagramService.deleteConfig();
    onSave();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Instagram Account ID *" value={formData.instagramAccountId} onChange={(v) => setFormData({ ...formData, instagramAccountId: v })} placeholder="17841400000000000" {...field.props('instagramAccountId')} />
        <FormField label="Connected Page ID *" value={formData.pageId} onChange={(v) => setFormData({ ...formData, pageId: v })} placeholder="1234567890" {...field.props('pageId')} />
        <FormField label="Username *" value={formData.username} onChange={(v) => setFormData({ ...formData, username: v })} placeholder="myinstagram" {...field.props('username')} />
        <PasswordField label="Access Token *" value={formData.accessToken} onChange={(v) => setFormData({ ...formData, accessToken: v })} show={showPasswords.accessToken} onToggle={() => setShowPasswords({ ...showPasswords, accessToken: !showPasswords.accessToken })} {...field.props('accessToken')} />
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={formData.enabled} onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })} className="h-4 w-4 text-blue-600" />
        <span className="text-sm">Enable Instagram channel</span>
      </div>
      {testResult && <TestResultBanner result={testResult} onDismiss={() => setTestResult(null)} />}
      <FormActions config={config} isSaving={isSaving} isTesting={isTesting} onTest={handleTest} onSave={handleSave} onDelete={handleDelete} errors={errors} />
    </div>
  );
}

// ==================== SMS Form ====================

function SmsForm({ config, onSave }: { config: SmsConfig | null; onSave: () => void }) {
  const [formData, setFormData] = useState({
    accountSid: '',
    authToken: '',
    phoneNumber: config?.phoneNumber || '',
    messagingServiceSid: config?.messagingServiceSid || '',
    webhookUrl: config?.webhookUrl || '',
    enabled: config?.enabled || false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  const errors = useMemo(() => validateSms(formData, !config), [formData, config]);
  const field = useFieldErrors(errors);

  const handleSave = async () => {
    if (hasErrors(errors)) return;
    try {
      setIsSaving(true);
      if (config) {
        await smsService.updateConfig(formData);
      } else {
        await smsService.createConfig(formData);
      }
      onSave();
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data?.message || 'Failed to save' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setIsTesting(true);
      const result = await smsService.testConnection();
      setTestResult(result);
    } catch (err: any) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setIsTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete SMS configuration?')) return;
    await smsService.deleteConfig();
    onSave();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Account SID *" value={formData.accountSid} onChange={(v) => setFormData({ ...formData, accountSid: v })} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" {...field.props('accountSid')} />
        <PasswordField label="Auth Token *" value={formData.authToken} onChange={(v) => setFormData({ ...formData, authToken: v })} show={showPasswords.authToken} onToggle={() => setShowPasswords({ ...showPasswords, authToken: !showPasswords.authToken })} {...field.props('authToken')} />
        <FormField label="Phone Number *" value={formData.phoneNumber} onChange={(v) => setFormData({ ...formData, phoneNumber: sanitizePhoneInput(v).replace(/[\s().-]/g, '') })} placeholder="+1234567890" {...field.props('phoneNumber')} />
        <FormField label="Messaging Service SID" value={formData.messagingServiceSid} onChange={(v) => setFormData({ ...formData, messagingServiceSid: v })} placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" {...field.props('messagingServiceSid')} />
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={formData.enabled} onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })} className="h-4 w-4 text-blue-600" />
        <span className="text-sm">Enable SMS channel</span>
      </div>
      {testResult && <TestResultBanner result={testResult} onDismiss={() => setTestResult(null)} />}
      <FormActions config={config} isSaving={isSaving} isTesting={isTesting} onTest={handleTest} onSave={handleSave} onDelete={handleDelete} errors={errors} />
    </div>
  );
}

// ==================== Shared Components ====================

/**
 * Welcome message + canned replies.
 *
 * Both are plain text, NOT Meta-approved templates: they are only ever sent
 * inside the 24-hour window a customer opens by writing in, which is exactly
 * the case Meta does not require a template for. That is why they can be
 * edited here and take effect immediately, with no review step.
 */
function WhatsAppAutoReplyForm() {
  const [welcomeEnabled, setWelcomeEnabled] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [cannedReplies, setCannedReplies] = useState<WhatsAppCannedReply[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    whatsappService
      .getAutoReply()
      .then((settings) => {
        if (cancelled) return;
        setWelcomeEnabled(settings.welcomeEnabled);
        setWelcomeMessage(settings.welcomeMessage);
        setCannedReplies(settings.cannedReplies);
      })
      .catch(() => {
        // No settings yet is not an error - the defaults above stand.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    // An enabled-but-empty welcome would silently send nothing.
    if (welcomeEnabled && !welcomeMessage.trim()) {
      setResult({ success: false, message: 'Write a welcome message, or switch it off.' });
      return;
    }
    const named = cannedReplies.filter((r) => r.title.trim() && r.body.trim());

    setIsSaving(true);
    setResult(null);
    try {
      const saved = await whatsappService.updateAutoReply({
        welcomeEnabled,
        welcomeMessage: welcomeMessage.trim(),
        cannedReplies: named,
      });
      setCannedReplies(saved.cannedReplies);
      setResult({ success: true, message: 'Auto-replies saved.' });
    } catch (err: any) {
      setResult({
        success: false,
        message: err.response?.data?.message || 'Failed to save auto-replies',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateReply = (index: number, patch: Partial<WhatsAppCannedReply>) => {
    setCannedReplies((prev) =>
      prev.map((reply, i) => (i === index ? { ...reply, ...patch } : reply)),
    );
  };

  if (isLoading) {
    return (
      <div className="mt-6 border-t pt-6">
        <Spinner size="sm" />
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4 border-t pt-6">
      <div>
        <h4 className="text-sm font-semibold">Welcome message &amp; canned replies</h4>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Plain text, no Meta approval needed. The welcome message is sent automatically the
          first time someone messages you, and again if they come back after the 24-hour window
          has closed.
        </p>
      </div>

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={welcomeEnabled}
          onChange={(e) => setWelcomeEnabled(e.target.checked)}
          className="h-4 w-4 text-blue-600"
        />
        <span className="text-sm">Send a welcome message automatically</span>
      </label>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
          Welcome message
        </label>
        <textarea
          value={welcomeMessage}
          onChange={(e) => setWelcomeMessage(e.target.value)}
          rows={4}
          maxLength={4096}
          placeholder="Hi! Thanks for contacting us. A member of our team will reply shortly."
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700"
        />
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
            Canned replies
          </label>
          <Button
            variant="outline"
            onClick={() =>
              setCannedReplies((prev) => [...prev, { id: '', title: '', body: '' }])
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            Add
          </Button>
        </div>

        {cannedReplies.length === 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            None yet. Canned replies appear in the composer on the WhatsApp page.
          </p>
        )}

        <div className="space-y-3">
          {cannedReplies.map((reply, index) => (
            <div
              key={reply.id || `new-${index}`}
              className="rounded-md border p-3 dark:border-gray-700"
            >
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={reply.title}
                  onChange={(e) => updateReply(index, { title: e.target.value })}
                  placeholder="Short label, e.g. Opening hours"
                  maxLength={80}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700"
                />
                <button
                  type="button"
                  aria-label="Remove canned reply"
                  onClick={() => setCannedReplies((prev) => prev.filter((_, i) => i !== index))}
                  className="flex-shrink-0 text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <textarea
                value={reply.body}
                onChange={(e) => updateReply(index, { body: e.target.value })}
                rows={2}
                maxLength={4096}
                placeholder="The message text"
                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700"
              />
            </div>
          ))}
        </div>
      </div>

      {result && <TestResultBanner result={result} onDismiss={() => setResult(null)} />}

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Check className="mr-2 h-4 w-4" />
          )}
          Save auto-replies
        </Button>
      </div>
    </div>
  );
}

/**
 * Meta-approved templates, synced from the Business Account.
 *
 * Read-only on purpose: templates are authored and reviewed in the Meta
 * dashboard, and mirroring that editor here would only let someone create
 * something Meta then rejects. What matters operationally is which templates
 * exist and which are actually sendable, so that is what this shows.
 */
function WhatsAppTemplatesPanel() {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    whatsappService
      .listTemplates()
      .then((rows) => {
        if (!cancelled) setTemplates(rows);
      })
      .catch(() => {
        // No templates yet is the normal starting state.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSync = async () => {
    setIsSyncing(true);
    setResult(null);
    try {
      const synced = await whatsappService.syncTemplates();
      setTemplates(synced.templates);
      setResult({ success: synced.success, message: synced.message });
    } catch (err: any) {
      setResult({
        success: false,
        message: err.response?.data?.message || 'Failed to sync templates',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="mt-6 space-y-4 border-t pt-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-semibold">Message templates</h4>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Approved templates are the only message WhatsApp allows outside the 24-hour
            window, so they are what starts or reopens a conversation. Authored and reviewed
            in the Meta dashboard; synced here. Needs the Business Account ID above.
          </p>
        </div>
        <Button variant="outline" onClick={handleSync} disabled={isSyncing}>
          {isSyncing ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Sync from Meta
        </Button>
      </div>

      {result && <TestResultBanner result={result} onDismiss={() => setResult(null)} />}

      {isLoading && <Spinner size="sm" />}

      {!isLoading && templates.length === 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          No templates synced yet.
        </p>
      )}

      <div className="space-y-2">
        {templates.map((template) => (
          <div
            key={template.id}
            className="rounded-md border p-3 dark:border-gray-700"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-sm font-medium">{template.name}</span>
              <span
                className={`flex-shrink-0 whitespace-nowrap rounded-full px-2 py-1 text-xs ${
                  template.isSendable
                    ? 'bg-green-100 text-green-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}
              >
                {template.language} · {template.status}
              </span>
            </div>
            <p className="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
              {template.bodyText}
            </p>
            {template.parameterCount > 0 && (
              <p className="mt-1 text-xs text-gray-400">
                {template.parameterCount} parameter
                {template.parameterCount === 1 ? '' : 's'}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * A field's problem is shown once the user has left it, so an untouched form
 * isn't a wall of red. Save stays disabled while any problem exists, touched
 * or not, and lists what is still missing.
 */
function useFieldErrors(errors: FieldErrors) {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  return {
    props: (name: string) => ({
      name,
      error: touched[name] ? errors[name] : undefined,
      onBlur: () => setTouched((t) => (t[name] ? t : { ...t, [name]: true })),
    }),
    reset: () => setTouched({}),
  };
}

interface FieldValidationProps {
  name?: string;
  error?: string;
  onBlur?: () => void;
}

const INPUT_BASE = 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2';
const inputTone = (error?: string) =>
  error
    ? 'border-red-400 focus:ring-red-500/40 dark:border-red-500/70'
    : 'border-gray-300 focus:ring-blue-500 dark:border-gray-700';

function FieldError({ id, error }: { id: string; error?: string }) {
  if (!error) return null;
  return (
    <p id={id} role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
      {error}
    </p>
  );
}

function FormField({ label, value, onChange, placeholder, name, error, onBlur }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string } & FieldValidationProps) {
  const id = `social-${name ?? label}`;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">{label}</label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`${INPUT_BASE} ${inputTone(error)}`}
      />
      <FieldError id={`${id}-error`} error={error} />
    </div>
  );
}

function PasswordField({ label, value, onChange, show, onToggle, name, error, onBlur }: { label: string; value: string; onChange: (v: string) => void; show: boolean; onToggle: () => void } & FieldValidationProps) {
  const id = `social-${name ?? label}`;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">{label}</label>
      <div className="relative">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder="Enter value"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={`${INPUT_BASE} pr-10 ${inputTone(error)}`}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={show ? `Hide ${label}` : `Show ${label}`}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      <FieldError id={`${id}-error`} error={error} />
    </div>
  );
}

function TestResultBanner({ result, onDismiss }: { result: { success: boolean; message: string }; onDismiss: () => void }) {
  // Auto-dismiss after 5s; the X button dismisses it manually.
  useAutoDismiss(result, onDismiss);
  return (
    <div className={`p-3 rounded-lg flex items-center gap-2 ${result.success ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'}`}>
      {result.success ? <Check className="h-4 w-4 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
      <span className="flex-1">{result.success ? 'Connection successful!' : result.message || 'Connection failed'}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className={`flex-shrink-0 ${result.success ? 'text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-200' : 'text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200'}`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function FormActions({ config, isSaving, isTesting, onTest, onSave, onDelete, errors }: { config: any; isSaving: boolean; isTesting: boolean; onTest: () => void; onSave: () => void; onDelete: () => void; errors: FieldErrors }) {
  const problems = Object.values(errors);
  return (
    <div className="space-y-3 pt-4 border-t">
      {problems.length > 0 && (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertCircle className="mt-px h-3.5 w-3.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Complete these to {config ? 'update' : 'save'}:</p>
            <ul className="mt-0.5 list-disc pl-4">
              {problems.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          {config && (
            <Button variant="outline" onClick={onDelete} className="text-red-600 border-red-300 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onTest} disabled={isTesting}>
            {isTesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TestTube2 className="h-4 w-4 mr-2" />}
            Test
          </Button>
          <Button onClick={onSave} disabled={isSaving || problems.length > 0}>
            {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            {config ? 'Update' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
